import React, { useEffect, useState, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import i18next from 'i18next';
import clsx from 'clsx';

import axiosInstance from '../config/axiosConfig';

import { ProgressSpinner } from 'primereact/progressspinner';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Avatar } from 'primereact/avatar';
import { Dialog } from 'primereact/dialog';
import { Tooltip } from 'primereact/tooltip';

import useUserStore from '../store/useUserStore';
import useSelectedCompanyStore from '../store/useSelectedCompanyStore';
import useCampaignStore from '../store/useCampaignStore';
import useStatisticsStore from '../store/useStatisticsStore';

import CompanyJoinDialog from './occasional/CompanyJoinDialog';
import WhatsNew from './occasional/WhatsNew';

import * as Sentry from "@sentry/react";

const t = (i18nKey, params) => i18next.t(`userDashboard.sidebar.${i18nKey}`, params);

const Sidebar = ({ hasNotification = false, isDisabled = false, isCollapsed, setIsCollapsed }) => {
    const companies = useUserStore((state) => state.companies);

    const setSelectedCompany = useSelectedCompanyStore((state) => state.setSelectedCompany);
    const selectedCompany = useSelectedCompanyStore((state) => state.selectedCompany);

    const clearCompanies = useUserStore((state) => state.clearCompanies);
    const clearSelectedCompany = useSelectedCompanyStore((state) => state.clearSelectedCompany);
    const clearCampaigns = useCampaignStore((state) => state.clearCampaigns);
    const clearAllData = useStatisticsStore((state) => state.clearAllData);

    const [isFeedbackDialogVisible, setIsFeedbackDialogVisible] = useState(false);
    const [feedbackName, setFeedbackName] = useState('');
    const [feedbackEmail, setFeedbackEmail] = useState('');
    const [feedbackMessage, setFeedbackMessage] = useState('');
    const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
    const [feedbackError, setFeedbackError] = useState('');

    const [isWhatsNewVisible, setIsWhatsNewVisible] = useState(false);

    const [toggleIsHovered, setToggleIsHovered] = useState(false);
    const [currentPath, setCurrentPath] = useState(null);
    const [companySelectLoading, setCompanySelectLoading] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);

    const [currentCompanyId, setCurrentCompanyId] = useState(null);
    const [filteredCompanies, setFilteredCompanies] = useState(companies);

    const op = useRef(null);
    const userPanelRef = useRef(null);

    const location = useLocation();
    const history = useHistory();

    const toggleSidebar = () => setIsCollapsed(!isCollapsed);

    const handleClearData = () => {
        clearCompanies();
        clearSelectedCompany();
        clearCampaigns();
        clearAllData();

        return true;
    };

    useEffect(() => {
        isCollapsed ? localStorage.setItem('sidebarIsCollapsed', isCollapsed) : localStorage.removeItem('sidebarIsCollapsed');
    }, [isCollapsed])

    const sidebarItemsRoutes = [
        '/dashboard',
        '/dashboard/campaigns',
        '/dashboard/company',
        '/dashboard/billing',
        '/dashboard/liana',
        '/dashboard/preferences'
    ];

    const handleKeyDown = (event) => {
        if (event.altKey && event.key === 'u') {
            toggleSidebar();
        }

        if (event.altKey) {
            if (event.key === 'ArrowUp') {
                const newIndex = (currentIndex - 1 + sidebarItemsRoutes.length) % sidebarItemsRoutes.length;
                setCurrentIndex(newIndex);
                history.push(sidebarItemsRoutes[newIndex]);
            } else if (event.key === 'ArrowDown') {
                const newIndex = (currentIndex + 1) % sidebarItemsRoutes.length;
                setCurrentIndex(newIndex);
                history.push(sidebarItemsRoutes[newIndex]);
            }
        }
    };

    const handleDoubleClick = (event) => {
        if (event.target.classList.contains('sidebar')) {
            toggleSidebar();
        }
    };

    useEffect(() => {
        const sidebarElement = document.querySelector('.sidebar');
        sidebarElement.addEventListener('dblclick', handleDoubleClick);

        return () => {
            sidebarElement.removeEventListener('dblclick', handleDoubleClick);
        };
    }, [isCollapsed]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isCollapsed, currentIndex]);

    useEffect(() => {
        setCurrentPath(location.pathname);
    }, [location.pathname])

    useEffect(() => {
        setFilteredCompanies(companies);
    }, [companies]);

    const onSearch = (event) => {
        if (!event.target.value.trim().length) {
            setFilteredCompanies([...companies]);
        } else {
            setFilteredCompanies(companies.filter((company) => {
                return company.company.name.toLowerCase().includes(event.target.value.toLowerCase());
            }));
        }
    };

    useEffect(() => {
        if (currentCompanyId) return;
        if (selectedCompany?.company_id) return;
        if (!companies || !companies.length || companies.length > 1) return;

        setCurrentCompanyId(companies[0].company_id);
    }, [companies]);

    useEffect(() => {
        if (!currentCompanyId) return;
        if (currentCompanyId === selectedCompany?.company_id) return;

        setCompanySelectLoading(true);

        axiosInstance.get(`/dashboard/companies/${currentCompanyId}`)
            .then(response => {
                const refresh_data = selectedCompany?.company_id !== response?.data?.company_id;
                setSelectedCompany(response.data);
                setCompanySelectLoading(false);

                if (refresh_data) return window.location.href = '/dashboard/', window.location.reload();

            }).catch(error => {
                console.error('Error fetching company data:', error);
                setCompanySelectLoading(false);
            });
    }, [currentCompanyId]);

    const navigateTo = (path) => {
        if (location.pathname === `/dashboard${path}`) {
            window.location.reload();
        } else {
            history.push(`/dashboard${path}`);
        }
    };

    const openFeedbackDialog = () => {
        setIsFeedbackDialogVisible(true);
    };

    const closeFeedbackDialog = () => {
        setIsFeedbackDialogVisible(false);
        setFeedbackName('');
        setFeedbackEmail('');
        setFeedbackMessage('');
        setFeedbackError('');
    };

    const handleFeedbackSubmit = async () => {
        if (!feedbackName || !feedbackEmail || !feedbackMessage) {
            setFeedbackError('Veuillez remplir tous les champs.');
            return;
        }

        setFeedbackSubmitting(true);
        setFeedbackError('');

        try {
            Sentry.captureFeedback({
                name: feedbackName,
                email: feedbackEmail,
                message: feedbackMessage,
            });

            closeFeedbackDialog();
        } catch (error) {
            console.error('Erreur lors de l\'envoi du feedback:', error);
            setFeedbackError('Une erreur est survenue. Veuillez réessayer plus tard.');
        } finally {
            setFeedbackSubmitting(false);
        }
    };

    const handleWhatsNewDialog = () => {
        setIsWhatsNewVisible(prev => !prev);
    };

    return (
        <>
            {/* If invitation_url, this is displayed */}
            <CompanyJoinDialog />

            <WhatsNew visible={isWhatsNewVisible} onHide={() => { setIsWhatsNewVisible(false) }} />

            {isCollapsed && <Tooltip target=".sidebar__menu-item.text-is-unselectable" disabled={false} showDelay={100} hideDelay={200} />}
            <div className={clsx('sidebar', { 'collapsed': isCollapsed, 'has-notification': hasNotification })}>

                <div className="sidebar__toggle" onClick={toggleSidebar}
                    onMouseEnter={() => setToggleIsHovered(true)}
                    onMouseLeave={() => setToggleIsHovered(false)}>
                    <i className={clsx({ 'fa-solid fa-grip-lines-vertical': !toggleIsHovered, 'fa-solid fa-chevron-right': toggleIsHovered && isCollapsed, 'fa-solid fa-chevron-left': toggleIsHovered && !isCollapsed })}></i>
                </div>

                <div className="sidebar__menu">

                    {companies.length > 0 &&
                        <div style={{ position: 'relative' }}>
                            {companySelectLoading &&
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}>
                                    <ProgressSpinner style={{ width: '50%', height: '50%' }} />
                                </div>
                            }

                            <div className={clsx('sidebar__menu-item text-is-unselectable mb-2', { 'is-disabled': companySelectLoading, 'is-highlighted': !selectedCompany })} onClick={(e) => op.current.toggle(e)} style={{ marginLeft: 3, marginRight: 3 }}
                                data-pr-tooltip={selectedCompany ? selectedCompany.name : 'Votre entreprise'}
                            >
                                <Avatar
                                    image={selectedCompany?.profile_picture ? `/dashboard/company/avatar/${selectedCompany?.profile_picture}` : '/images/dashboard/company-logo-placeholder.png'}
                                    label={`${selectedCompany?.name?.slice(0, 2)}`}
                                    size="large"
                                    shape="circle"
                                    style={{ width: '30px', height: '30px' }}
                                    onClick={(e) => { userPanelRef?.current && userPanelRef.current.toggle(e) }}
                                />

                                <span style={{ textOverflow: 'ellipsis', overflowX: 'hidden' }}>{selectedCompany ? selectedCompany.name : 'Choisir une entreprise'}</span>
                            </div>

                            <OverlayPanel ref={op} className="sidebar__overlay-panel">
                                {companies.length > 5 && (
                                    <div className="search-bar">
                                        <div className="field">
                                            <p className="control has-icons-left">
                                                <input className="input" type="text" placeholder="Rechercher..." onChange={onSearch} />
                                                <span className="icon is-small is-left">
                                                    <i className="fa-solid fa-magnifying-glass"></i>
                                                </span>
                                            </p>
                                        </div>
                                    </div>)}

                                {filteredCompanies.map((company) => (
                                    <div key={company.company_id} className={clsx('p-listbox p-component mb-1', { 'is-hidden': selectedCompany?.company_id === company?.company_id })} style={{ maxHeight: '500px', overflowY: 'auto' }}>
                                        <div className="p-listbox-item" onClick={() => { handleClearData(); setCurrentCompanyId(company?.company_id); op.current.hide(); }}>
                                            <Avatar
                                                image={company.company.profile_picture ? `/dashboard/company/avatar/${company.company.profile_picture}` : '/images/dashboard/company-logo-placeholder.png'}
                                                label={`${company?.company?.name?.slice(0, 2)}`}
                                                size="large"
                                                shape="circle"
                                                style={{ width: '30px', height: '30px' }}
                                                onClick={(e) => { userPanelRef?.current && userPanelRef.current.toggle(e) }}
                                            />

                                            <span className="company-name ml-1">{company.company.name}</span>
                                        </div>
                                    </div>
                                ))}

                                <div className={clsx('p-listbox-item', { 'mt-3': filteredCompanies?.length > 1 })} onClick={() => { window.location.href = '/dashboard/company/create' }}>
                                    <i className='fa-solid fa-circle-plus' />
                                    <span className="company-name ml-1">Nouvelle entreprise </span>
                                </div>

                            </OverlayPanel>

                            <div className="sidebar__divider"></div>
                        </div>
                    }

                    <div className={clsx('sidebar__menu-item text-is-unselectable', { 'is-disabled': isDisabled, 'is-active': location.pathname.endsWith('/dashboard') || location.pathname.endsWith('/dashboard/') })} onClick={() => { navigateTo('/') }}
                        data-pr-tooltip={t('dashboard')}
                    >
                        <div>
                            <i className="fa-solid fa-house"></i>
                            <span>{t('dashboard')}</span>
                        </div>
                    </div>

                    <div className={clsx('sidebar__menu-item text-is-unselectable', { 'is-disabled': isDisabled, 'is-active': location.pathname.includes('campaigns') })} onClick={() => navigateTo('/campaigns')}
                        data-pr-tooltip={t('my_campaigns')}
                    >
                        <div>
                            <i className="fa-solid fa-list"></i>
                            <span>{t('my_campaigns')}</span>
                        </div>
                    </div>

                    <div className={clsx('sidebar__menu-item text-is-unselectable', { 'is-disabled': isDisabled, 'is-active': location.pathname.includes('company') })} onClick={() => navigateTo('/company')}
                        data-pr-tooltip={t('my_companies')}
                    >
                        <div>
                            <i className="fa-solid fa-building"></i>
                            <span>{t('my_companies')}</span>
                        </div>
                    </div>

                    <div className={clsx('sidebar__menu-item text-is-unselectable', { 'is-disabled': isDisabled, 'is-active': location.pathname.includes('billing') })} onClick={() => navigateTo('/billing')}
                        data-pr-tooltip={t('billing')}
                    >
                        <div>
                            <i className="fa-solid fa-tag"></i>
                            <span>{t('billing')}</span>
                        </div>
                    </div>

                    <div className="sidebar__divider"></div>

                    <div className={clsx('sidebar__menu-item text-is-unselectable', { 'is-disabled': isDisabled, 'is-active': isWhatsNewVisible })} onClick={handleWhatsNewDialog}
                        data-pr-tooltip={t('whats_new')}
                    >
                        <div>
                            <i className="fa-solid fa-clock-rotate-left"></i>
                            <span>{t('whats_new')}</span>
                        </div>
                    </div>

                    <div id="feedback-item" onClick={openFeedbackDialog} className={clsx('sidebar__menu-item text-is-unselectable', { 'is-disabled': isDisabled })}
                        data-pr-tooltip={t('report_bug.title')}
                    >
                        <div>
                            <i className="fa-solid fa-bug"></i>
                            <span>{t('report_bug.title')}</span>
                        </div>
                    </div>

                    <div className="sidebar__menu-item text-is-unselectable" onClick={() => { window.open('/contact', '_blank'); }}
                        data-pr-tooltip={t('contact_us')}
                    >
                        <div>
                            <i className="fa-regular fa-envelope"></i>
                            <span>{t('contact_us')} <i className='fa-solid fa-arrow-up-right-from-square ml-2 hover-icon'></i></span>
                        </div>
                    </div>

                </div>

                <div className="sidebar__footer">

                    <div className="sidebar__menu">

                        <div className={clsx('sidebar__menu-item text-is-unselectable has-img mb-2', { 'is-disabled': isDisabled, 'is-active': location.pathname.includes('liana') })}
                            onClick={() => { navigateTo('/liana') }}
                            style={{ paddingLeft: 20 }}
                            data-pr-tooltip={'LiAna.'}
                        >

                            <img src="/images/dashboard/liana.svg" style={{ height: '25px' }} alt="LiAna Logo" />
                            <span>LiAna</span>
                        </div>

                        <div className={clsx('sidebar__menu-item text-is-unselectable', { 'is-disabled': isDisabled, 'is-active': location.pathname.includes('preferences') })} onClick={() => { navigateTo('/preferences') }}
                            data-pr-tooltip={t('preferences')}
                        >
                            <div>
                                <i className="fa-solid fa-filter"></i>
                                <span>{t('preferences')}</span>
                            </div>
                        </div>

                    </div>

                    <div className={clsx('sidebar__footer-links', { 'is-invisible': isCollapsed })}>
                        <a href="#" className='text-is-unselectable'>{t('footer.tos')}</a>
                        <a href="#" className='text-is-unselectable'>{t('footer.privacy')}</a>
                        <a href="#" className='text-is-unselectable'>{t('footer.help')}</a>
                    </div>

                </div>

                <Dialog
                    header={t('report_bug.title')}
                    visible={isFeedbackDialogVisible}
                    style={{ width: '400px' }}
                    onHide={closeFeedbackDialog}
                    modal
                    dismissableMask
                    className="feedback-dialog"
                >
                    <div className="field">
                        <label className="label" htmlFor="feedbackName">{t('report_bug.name')}</label>
                        <div className="control">
                            <input
                                className="input"
                                id="feedbackName"
                                type="text"
                                value={feedbackName}
                                onChange={(e) => setFeedbackName(e.target.value)}
                                placeholder={t('report_bug.name_placeholder')}
                            />
                        </div>
                    </div>
                    <div className="field">
                        <label className="label" htmlFor="feedbackEmail">{t('report_bug.email')}</label>
                        <div className="control">
                            <input
                                className="input"
                                id="feedbackEmail"
                                type="email"
                                value={feedbackEmail}
                                onChange={(e) => setFeedbackEmail(e.target.value)}
                                placeholder={t('report_bug.email_placeholder')}
                            />
                        </div>
                    </div>
                    <div className="field">
                        <label className="label" htmlFor="feedbackMessage">{t('report_bug.message')}</label>
                        <div className="control">
                            <textarea
                                className="textarea"
                                id="feedbackMessage"
                                value={feedbackMessage}
                                onChange={(e) => setFeedbackMessage(e.target.value)}
                                placeholder={t('report_bug.message_placeholder')}
                                rows={5}
                            />
                        </div>
                    </div>
                    {feedbackError && <p className="help is-danger">{feedbackError}</p>}
                    <div className="field is-grouped is-justify-content-center">
                        <p className="control">
                            <button
                                className="button is-light"
                                onClick={closeFeedbackDialog}
                                disabled={feedbackSubmitting}
                            >
                                Annuler
                            </button>
                        </p>
                        <p className="control">
                            <button className={`button is-info is-light is-outlined ${feedbackSubmitting ? 'is-loading' : ''}`} onClick={handleFeedbackSubmit}>
                                Envoyer
                            </button>
                        </p>
                    </div>
                </Dialog>

            </div>
        </>
    )
}

export default Sidebar;